import { Component, OnInit, viewChild } from '@angular/core';
import * as MenuActions from '@states/menu/menu.actions';
import { MenuKey } from '@enums/menu.enum';
import { select, Store } from '@ngrx/store';
import { FormControlStatus, ReactiveFormsModule } from '@angular/forms';
import { catchError, concatMap, filter, first, tap, throwError } from 'rxjs';
import { LocationEditSelectors } from '@states/location-edit/location-edit.selector-types';
import { LocationModel } from '../../../locations/location.model';
import { LocationsService } from '../../../locations/locations.service';
import { SharedActions } from '@states/shared/shared.action-types';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { LocationEditActions } from '@states/location-edit/location-edit.action-types';
import { LocationEditEffects } from '@effects/location-edit.effects';
import { LocationActions } from '@states/location/location.action-types';
import {
  ConfirmDialogOptions,
  ConfirmDialogSelection,
  ConfirmDialogType,
} from '../../../shared/confirm-dialog/confirm-dialog.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmDialogService } from '../../../shared/confirm-dialog/confirm-dialog.service';
import { routerSegments } from '@consts/routes';
import { PreloaderColor } from '@enums/shared.enum';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatLabel } from '@angular/material/input';
import { UiKitModule } from '../../../shared/ui-kit/ui-kit.module';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { AddLocationFromComponent } from '../../../locations/components/add-location-from/add-location-from.component';
import LocationItem = LocationModel.LocationItem;

@UntilDestroy()
@Component({
  selector: 'app-location-details',
  templateUrl: './location-details.component.html',
  styleUrls: ['./location-details.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatLabel,
    TranslatePipe,
    UiKitModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatIconModule,
    AddLocationFromComponent,
  ],
})
export class LocationDetailsComponent implements OnInit {
  protected form = viewChild<AddLocationFromComponent>('form');

  location: LocationItem;
  deleteProcess = false;

  protected readonly PreloaderColor = PreloaderColor;

  constructor(
    private store$: Store,
    private locationsService: LocationsService,
    private locationEditEffects: LocationEditEffects,
    private router: Router,
    private confirm: ConfirmDialogService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.store$.dispatch(
      MenuActions.setActiveLevel2Menu({
        activeLevel2: MenuKey.details,
      }),
    );

    this.store$
      .pipe(select(LocationEditSelectors.selectedLocation), untilDestroyed(this))
      .subscribe((location: LocationItem) => {
        if (!!location) {
          this.location = location;
        }
      });

    this.locationEditEffects.pressSave$
      .pipe(untilDestroyed(this), ofType(LocationEditActions.pressSave))
      .subscribe((_) => {
        this.store$.dispatch(SharedActions.setIsSaving({ isSaving: true }));
        this.editLocation();
      });

    this.checkFormValidity();
  }

  onLocationDeleteError(message = this.translateService.instant('TOASTS.UNKNOWN_ERROR_OCCURRED')) {
    this.store$.dispatch(SharedActions.showMessage({ error: message }));
    // this.router.navigate([routerSegments.locationV2, routerSegments.list]);
  }

  onLocationDeleteSuccess(message = 'operation completed successfully') {
    this.store$.dispatch(SharedActions.showMessage({ success: message }));
    this.store$.dispatch(SharedActions.setIsDeleting({ isDeleting: true }));
    this.router.navigate([routerSegments.locationV2, routerSegments.list]);
  }

  deleteLocation() {
    const locationId = this.location._id;
    const confirmation$ = (options: ConfirmDialogOptions) =>
      this.confirm.open(options).pipe(
        untilDestroyed(this),
        catchError((err) => throwError(() => err)),
      );

    this.locationsService
      .deleteLocationAllowed(locationId)
      .pipe(
        untilDestroyed(this),
        concatMap((allowed) => {
          if (!allowed) {
            return confirmation$({
              type: ConfirmDialogType.INFO,
              msg: `Cant delete location that contain cores. please remove all edges from location first`,
              title: `Delete location ${name}`,
              confirm: 'Yes',
              disableClose: true,
            });
          } else {
            return confirmation$({
              type: ConfirmDialogType.CONFIRM,
              msg: `Are you sure you want to delete location '${name}?'`,
              title: `Delete location ${name}`,
              confirm: 'Yes',
              disableClose: true,
            });
          }
        }),
        filter((res) => res.selection == ConfirmDialogSelection.OK && res.type === ConfirmDialogType.CONFIRM),
        tap((res) => this.store$.dispatch(LocationActions.DeleteLocation({ request: { locationId } }))),
        concatMap((res) => this.locationsService.deleteLocationSuccess(locationId)),
        filter((res) => res),
        first(),
      )
      .subscribe({
        complete: () => {
          this.onLocationDeleteSuccess();
        },
        next: (val) => {},
        error: (err: HttpErrorResponse | Error) => {
          let msg =
            err instanceof HttpErrorResponse
              ? (err as HttpErrorResponse)?.error?.message
              : (err as Error)?.message;

          this.onLocationDeleteError(msg);
        },
      });
  }

  editLocation() {
    const location = this.form().locationFormGroup.getRawValue();
    const request = {
      ...location,
    } as LocationModel.LocationCreateRequest;

    return this.locationsService
      .updateLocation(request)
      .pipe(
        catchError((err) => {
          this.store$.dispatch(SharedActions.setIsSaving({ isSaving: false }));
          return err;
        }),
      )
      .subscribe((res) => {
        this.store$.dispatch(
          LocationActions.CreateLocationNoBackendCall({
            request: {
              ...request,
              _id: location._id,
            },
          }),
        );
        this.store$.dispatch(SharedActions.showMessage({ success: 'Location updated successfully' }));
        this.store$.dispatch(SharedActions.setIsSaving({ isSaving: false }));
      });
  }

  private checkFormValidity(): void {
    this.form().locationFormGroup.statusChanges
      .pipe(untilDestroyed(this))
      .subscribe((status: FormControlStatus) => {
        if (status === 'VALID') {
          this.store$.dispatch(LocationEditActions.changeDetailsFormValidation({ isFormValid: true }));
        } else {
          this.store$.dispatch(LocationEditActions.changeDetailsFormValidation({ isFormValid: false }));
        }
      });
  }
}
